import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, euro, dataIt, STATI_PREVENTIVO, formatError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

const HEAD = ["N°", "Cliente", "Bici", "Data", "Valido fino al", "Stato", "Totale", ""];

export const PrevBadge = ({ stato }) => {
  const s = STATI_PREVENTIVO.find((x) => x.value === stato) || STATI_PREVENTIVO[0];
  return <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${s.cls}`} data-testid={`prev-badge-${stato}`}>{s.label}</span>;
};

export const scadenza = (p) => {
  if (!p.data) return "";
  const d = new Date(`${p.data}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + (Number(p.validita_giorni) || 30));
  return d.toISOString().slice(0, 10);
};

export default function Preventivi() {
  const navigate = useNavigate();
  const [stato, setStato] = useState("tutti");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/preventivi", { params: { stato: stato === "tutti" ? "" : stato, q } })
        .then((r) => setItems(r.data)).catch((e) => toast.error(formatError(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [stato, q]);

  return (
    <div>
      <PageHeader overline="Officina" title="Preventivi di riparazione">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="preventivi-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="N°, cliente, bici..." className="w-56 bg-white pl-9" />
        </div>
        <Button data-testid="preventivi-new-button" onClick={() => navigate("/preventivi/nuovo")} className="bg-orange-600 hover:bg-orange-700"><Plus className="mr-1 h-4 w-4" /> Nuovo preventivo</Button>
      </PageHeader>
      <Tabs value={stato} onValueChange={setStato} className="mb-4">
        <TabsList className="flex h-auto flex-wrap justify-start bg-white ring-1 ring-slate-200">
          <TabsTrigger value="tutti" data-testid="prev-filter-tutti-tab">Tutti</TabsTrigger>
          {STATI_PREVENTIVO.map((s) => <TabsTrigger key={s.value} value={s.value} data-testid={`prev-filter-${s.value}-tab`}>{s.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <Table data-testid="preventivi-table">
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {HEAD.map((h) => <TableHead key={h} className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/preventivi/${p.id}`)} data-testid={`preventivi-row-${p.id}`}>
                <TableCell className="font-mono text-xs font-bold text-orange-600">{p.numero}</TableCell>
                <TableCell className="font-semibold text-slate-900">{p.cliente_nome}</TableCell>
                <TableCell>{p.bici_desc}</TableCell>
                <TableCell>{dataIt(p.data)}</TableCell>
                <TableCell>{dataIt(scadenza(p))}</TableCell>
                <TableCell><PrevBadge stato={p.stato} /></TableCell>
                <TableCell className="font-mono font-bold">{euro(p.totale)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" onClick={() => navigate(`/preventivi/${p.id}/stampa`)} data-testid={`preventivi-print-${p.id}`} title="Stampa"><Printer className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="py-16 text-center text-sm text-slate-500" data-testid="preventivi-empty">Nessun preventivo trovato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
